import {useEffect, useState, type RefObject} from 'react'

/**
 * Custom hook to determine if a tooltip should be shown for truncated file names.
 * This hook checks whenever the element is hovered for whether it is large enough to require a tooltip.
 */
export function useFileNameTooltip(headingRef: RefObject<HTMLElement | null>) {
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const heading = headingRef.current
    if (!heading) return

    const showIfOverflowing = () => {
      setIsOverflowing(heading.scrollWidth > heading.offsetWidth)
    }

    const handleFocus = () => showIfOverflowing()
    const handleBlur = () => setIsOverflowing(false)
    const handleMouseEnter = () => showIfOverflowing()
    const handleMouseLeave = () => setIsOverflowing(false)

    heading.addEventListener('focus', handleFocus)
    heading.addEventListener('blur', handleBlur)
    heading.addEventListener('mouseenter', handleMouseEnter)
    heading.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      heading.removeEventListener('focus', handleFocus)
      heading.removeEventListener('blur', handleBlur)
      heading.removeEventListener('mouseenter', handleMouseEnter)
      heading.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [headingRef])

  return isOverflowing
}
